import { create } from 'zustand';

import { subscribeWithSelector } from 'zustand/middleware';

import { CommonAppDataStore, makeCommonAppDataStore } from './CommonAppDataStore';

import { WidgetStore, makeWidgetStore } from './WidgetStore';

import { useAdminApi } from '@hooks/useAdminApi';
import { useApi } from '@hooks/useApi';
import ApiClient from '@services/ApiClient';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { useUserProductsAndTenorsContext } from '@shared/contexts/UserProductsAndTenorsProvider';
import { useEffect } from 'react';

export type AppStore = CommonAppDataStore & WidgetStore;

export const useAppStore = create<AppStore>()(
  subscribeWithSelector((...args) => {
    return {
      ...makeWidgetStore(...args),
      ...makeCommonAppDataStore(...args),
    };
  })
);

// Hook to get the store for a widget by ID
export function useWidgetStore<T>(widgetId: string): T {
  try {
    return useAppStore(state => state.widgets[widgetId].storeHook)() as T;
  } catch (e) {
    return {} as T;
  }
}

// Hook to initialse the store
export const useAppStoreInitHook = () => {
  const { apiClient } = useApi();
  const user = useUserContext();
  const { userProducts: products, tenors } = useUserProductsAndTenorsContext();
  const { setCommonData } = useAppStore();

  let adminApiClient: ApiClient | null | false = null;

  try {
    const { apiClient: maybeAdminApi } = useAdminApi();
    adminApiClient = maybeAdminApi || null;
  } catch (e) {
    adminApiClient = false;
  }

  useEffect(() => {
    setCommonData({ apiClient, products, tenors, user, adminApiClient });
  }, [apiClient, products, tenors, user, adminApiClient]);
};
